/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Typography, Collapse, Divider, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ColorPicker from "components/ColorPicker";
import ContentShell from "components/ContentShell";
import React, { useEffect, useState } from "react";
import PlayerCardCanvas from "components/PlayerCardCanvas";
import { sendMessageToCanvas } from "utils/miscUtils";
import { SimpleTextInput } from "components/FormTextInput";
import { useSnackbar } from "hooks/useSnackbar";
import { useLocation } from "react-router-dom";
import { defaultCardDetails } from "utils/constants";
import {
  updateClub,
  getInfoClub,
  createClub,
  uploadClubJerseys,
} from "services/playerCardService";
import FilePicker from "components/FilePicker";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const useStyles = makeStyles((theme) => ({
  submitButton: {
    width: 230,
    backgroundColor: "#1D1030",
    color: "white",
    fontWeight: "bold",
    marginTop: 10,
  },
  playerClubEditor: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "center",
    gap: "25px",
    margin: "0 auto",
    padding: "20px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      alignItems: "center",
      gap: 25,
    },
    [theme.breakpoints.up("sm")]: {
      gap: 30,
    },
  },
  clubSelectionContainer: {
    position: "relative",
  },
  clubForm: {
    marginTop: -5,
    width: "100%",
    maxWidth: 500,
  },
  jerseyUploadContainer: {
    marginTop: 20,
    padding: 15,
    border: "1px dashed #ccc",
    borderRadius: 4,
    backgroundColor: "#fff",
  },
  uploadHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: 10,
  },
  structureGuide: {
    marginTop: 15,
    padding: 15,
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
  },
  structureTitle: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    marginBottom: 5,
  },
  folderStructure: {
    fontFamily: "monospace",
    whiteSpace: "pre",
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: 10,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: 13,
    marginTop: 5,
  },
  successText: {
    color: theme.palette.success.main,
    fontSize: 13,
    marginTop: 5,
  },
}));

const EditClub = () => {
  const location = useLocation();
  const id = location.pathname.split("/").slice(-1)[0];
  const [loading, setLoading] = useState(true);
  const [playerDetails, setPlayerDetails] = useState<{
    clubName: any;
    clubAbbrName: any;
    externalClubId: any;
    gender: string;
    fullName: string;
    commonName: string;
    displayName: string;
    displayNameAbb: string;
    overallRating: string;
    positionName: string;
    position?: string;
    cardType: string;
    clubTextColor: string;
    clubBGColor: string;
    clubShortCode?: string;
    nationality: string;
    attackRating: number;
    defenceRating: number;
    creativityRating: number;
    playerFaceImage: any;
    face: string;
    emblem: string;
    textColor: string;
    positionBGColor: string;
    imageUrl: string;
    nationalityImageUrl: string;
    isJerseyUpload?: boolean;
  }>({
    ...defaultCardDetails,
    clubName: null,
    clubAbbrName: null,
    externalClubId: null,
    gender: 'M'
  });
  const [jerseyZipFile, setJerseyZipFile] = useState(null);
  const [showStructure, setShowStructure] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const styles = useStyles();
  const { showSnackbar, handleClose } = useSnackbar();

  const sendToCanvas = (type, data) => {
    const iframe = document.getElementById("playerCard");
    if (iframe) {
      sendMessageToCanvas(iframe, {
        message: "UPDATE_PLAYER",
        type: type === "INIT" ? "INIT" : type,
        data: data,
      });
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e.target;
    let data = {
      ...playerDetails,
      [name]: value,
    };
    sendToCanvas(name, { value });
    setPlayerDetails(data);
  };

  const handleJerseyZipSelect = (file: any) => {
    if (!file) {
      setJerseyZipFile(null);
      setValidationError(null);
      return;
    }

    if (
      file.type !== "application/zip" &&
      file.type !== "application/x-zip-compressed" &&
      !file.name.endsWith(".zip")
    ) {
      setValidationError("Please upload a valid ZIP file");
      return;
    }

    setJerseyZipFile(file);
    setValidationError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    let closeSnackbarOnErrId: number = 0;
    try {
      const payload: any = {
        name: playerDetails.clubName,
        abbreviation: playerDetails.clubAbbrName,
        textColor: playerDetails.clubTextColor,
        backgroundColor: playerDetails.clubBGColor,
        externalClubId: playerDetails.externalClubId?.trim(),
        gender: playerDetails.gender
      };

      if (id !== "create") {
        payload._id = id;
        payload.updateAllPlayerRecord = true;
      }

      const response =
        id === "create" ? await createClub(payload) : await updateClub(payload);

      showSnackbar(
        response?.message || "Operation completed successfully",
        "success"
      );

      if (jerseyZipFile || (id !== 'create' && response.data.isUpdatedExternalId)) {
        const formData = new FormData();

        if (jerseyZipFile) {
          closeSnackbarOnErrId = showSnackbar(
            'Starting jersey zip upload. Please be patient, this may take some time to complete.',
            "info",
            8000
          );
        } else if (response.data.isUpdatedExternalId) {
          closeSnackbarOnErrId = showSnackbar(
            'ExternalId changed. Moving the jerseys under the new ExternalId. Please be patient, this may take some time to complete.',
            "info",
            8000
          );
        }

        formData.append("file", jerseyZipFile);
        formData.append("clubId", response.data._id);

        if (response.data.isUpdatedExternalId !== undefined && response.data.isUpdatedExternalId !== null) {
          formData.append("isUpdatedExternalId", response.data.isUpdatedExternalId);
        }

        if (response.data.oldExternalId !== undefined && response.data.oldExternalId !== null) {
          formData.append("oldExternalId", response.data.oldExternalId);
        }



        const resp = await uploadClubJerseys(formData);

        setJerseyZipFile(null);
        setValidationError(null);

        showSnackbar(
          resp.data.message ?? resp.message ?? "The upload of the Jersey ZIP completed.",
          "success"
        );
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.details ||
        error?.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);

      if (closeSnackbarOnErrId) {
        handleClose(closeSnackbarOnErrId)
      }
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (id !== "create") {
        const response = await getInfoClub(id);

        const { data } = response;

        setPlayerDetails((prevDetails) => ({
          ...prevDetails,
          clubName: data?.name,
          clubAbbrName: data?.abbreviation,
          clubTextColor: data?.textColor,
          clubBGColor: data?.backgroundColor,
          externalClubId: data?.externalClubId || "",
          isJerseyUpload: data?.isJerseyUpload || false,
          gender: data?.gender
        }));
      }
    } catch (error) {
      showSnackbar(error ?? error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <ContentShell
      showBackButton
      loading={loading}
      title={id === "create" ? "Create Club" : "Edit Club"}
    >
      <Box className={styles.playerClubEditor}>
        <Box>
          <Box className={styles.clubSelectionContainer}>
            <SimpleTextInput
              type="text"
              label="Club Name"
              name="clubName"
              value={playerDetails.clubName}
              onChange={onChange}
            />
          </Box>
          <br />
          <Box>
            <PlayerCardCanvas
              playerDetails={playerDetails}
              sendToCanvas={sendToCanvas}
            />
          </Box>
        </Box>

        <Box className={styles.clubForm}>
          <SimpleTextInput
            type="text"
            label="Club Abbreviation Name"
            name="clubAbbrName"
            value={playerDetails.clubAbbrName}
            onChange={onChange}
            sx={{ mb: 2 }}
          />

          <SimpleTextInput
            type="text"
            label="External ID"
            name="externalClubId"
            value={playerDetails.externalClubId}
            onChange={onChange}
            required
            sx={{ mb: 2 }}
          />

          <ColorPicker
            label={"Club Text Color"}
            name="clubTextColor"
            value={playerDetails.clubTextColor}
            onChange={onChange}
            customStyle={{ width: 230, marginBottom: 7, marginTop: 10 }}
          />

          <ColorPicker
            label={"Club Bg Color"}
            name="clubBGColor"
            value={playerDetails.clubBGColor}
            onChange={onChange}
            customStyle={{ width: 230, marginBottom: 7 }}
          />

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">Gender</FormLabel>
            <RadioGroup
              row
              name="gender"
              value={playerDetails.gender || "M"}
              onChange={onChange}
            >
              <FormControlLabel value="M" control={<Radio />} label="Male" />
              <FormControlLabel value="F" control={<Radio />} label="Female" />
            </RadioGroup>
          </FormControl>

          <Box className={styles.jerseyUploadContainer}>
            <Box className={styles.uploadHeader}>
              <InfoIcon color="primary" style={{ marginRight: 8 }} />
              <Typography variant="subtitle1">Jerseys (Optional)</Typography>
            </Box>

            <Divider style={{ margin: "10px 0" }} />

            <FilePicker
              label="Select Jersey ZIP File"
              name="jerseyZip"
              onFileSelect={handleJerseyZipSelect}
              strict
              showPreview={false}
            />

            {validationError && (
              <Typography className={styles.errorText}>
                {validationError}
              </Typography>
            )}

            {jerseyZipFile && !validationError && (
              <Typography className={styles.successText}>
                {jerseyZipFile.name} selected
              </Typography>
            )}

            {id !== "create" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: 2,
                  padding: 1,
                  borderRadius: 1,
                  backgroundColor: playerDetails.isJerseyUpload
                    ? "rgba(46, 125, 50, 0.1)"
                    : "rgba(211, 47, 47, 0.1)",
                  borderLeft: `4px solid ${playerDetails.isJerseyUpload ? "#2e7d32" : "#d32f2f"
                    }`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "medium",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: playerDetails.isJerseyUpload
                        ? "#2e7d32"
                        : "#d32f2f",
                    }}
                  />
                  Jersey Upload Status:
                  <Box
                    component="span"
                    sx={{
                      fontWeight: "bold",
                      color: playerDetails.isJerseyUpload
                        ? "#2e7d32"
                        : "#d32f2f",
                    }}
                  >
                    {playerDetails.isJerseyUpload ? "Uploaded" : "Not Uploaded"}
                  </Box>
                </Typography>
              </Box>
            )}

            <Box className={styles.structureGuide}>
              <Box
                className={styles.structureTitle}
                onClick={() => setShowStructure(!showStructure)}
              >
                {showStructure ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                <Typography variant="body2" style={{ marginLeft: 5 }}>
                  ZIP Folder Structure Guide
                </Typography>
              </Box>

              <Collapse in={showStructure}>
                <Typography variant="caption" component="div">
                  Your ZIP file should contain jersey images directly at the root level:
                </Typography>
                <Box className={styles.folderStructure}>
                  {`<upload.zip>/
├── 1.png
├── 2.png
├── 3.png
├── 4.png
└── ...`}
                </Box>
              </Collapse>
            </Box>
          </Box>

          <Button
            variant="contained"
            className={styles.submitButton}
            onClick={handleSubmit}
            style={{ marginTop: 20 }}
            disabled={loading}
          >
            {id === "create" ? "Create Club" : "Update Club"}
          </Button>
        </Box>
      </Box>
    </ContentShell>
  );
};

export default EditClub;
